/**
 * Trailing-debounced wrapper for `setDerivedMarkdown`.
 *
 * Markdown is a derived string used for export, attachment-reference
 * scanning, and snapshot persistence — never load-bearing for live collab.
 * Running `htmlToMarkdown(ed.getHTML())` on every PM transaction (which the
 * old `onUpdate` did) traversed the whole DOM with Turndown for each
 * keystroke; the cost compounded with marks/atoms and on remote yjs apply.
 *
 * This helper coalesces calls to once per `DEBOUNCE_MS` per docId. The
 * snapshot persistence path (3 s / 30 s debounce in `persistSnapshot`) still
 * picks up the freshest markdown — at most this debounce is one snapshot
 * cycle behind, never more.
 *
 * Doc switches MUST `flushDerivedMarkdown(prevDocId)` before tearing down so
 * the prev doc's last edits land in the next snapshot.
 */
import { htmlToMarkdown } from './markdown';
import { setDerivedMarkdown } from './yjsCollab';

const DEBOUNCE_MS = 1000;

interface Pending {
  timer: ReturnType<typeof setTimeout>;
  /** Captured HTML at the most recent schedule call. Coalescing N keystrokes
   * within `DEBOUNCE_MS` means the last keystroke's HTML wins — equivalent to
   * a `getHtml()` deferred call but doesn't require the editor to still be
   * alive at flush time (which matters for doc-switch / unmount flushes). */
  html: string;
}

const pending = new Map<string, Pending>();

function run(docId: string, html: string): void {
  try {
    setDerivedMarkdown(docId, htmlToMarkdown(html));
  } catch (err) {
    console.warn('vault: derivedMarkdown turndown failed', err);
  }
}

export function scheduleDerivedMarkdown(docId: string, html: string): void {
  if (!docId) return;
  const existing = pending.get(docId);
  if (existing) clearTimeout(existing.timer);
  const timer = setTimeout(() => {
    const p = pending.get(docId);
    if (!p) return;
    pending.delete(docId);
    run(docId, p.html);
  }, DEBOUNCE_MS);
  pending.set(docId, { timer, html });
}

/** Run any pending derivation for this doc immediately. Call before tearing
 * down a doc (doc switch, unmount) and before reading the doc's markdown for
 * export so the very latest text lands in the snapshot. Cheap no-op when
 * nothing's pending. */
export function flushDerivedMarkdown(docId: string): void {
  const p = pending.get(docId);
  if (!p) return;
  clearTimeout(p.timer);
  pending.delete(docId);
  run(docId, p.html);
}

/** Cancel any pending derivation without running it. Use when the underlying
 * doc state is being torn down and you don't need the markdown to land. */
export function cancelDerivedMarkdown(docId: string): void {
  const p = pending.get(docId);
  if (!p) return;
  clearTimeout(p.timer);
  pending.delete(docId);
}

/** Flush every pending derivation. Used by Workspace pre-sign-out / pre-lock
 * flows so background derivations don't outlive the editor. */
export function flushAllDerivedMarkdown(): void {
  for (const docId of Array.from(pending.keys())) {
    flushDerivedMarkdown(docId);
  }
}
